import {
    collection,
    component,
    dateField,
    EntityData,
    field,
    identifier,
    NumberUtils,
    transientField
} from "@fedeltapointofsale/data-client";

export class ReasonCodeData extends EntityData<ReasonCodeData> {
    @field code: string;
}

export class ReasonData extends EntityData<ReasonData> {
    @field reasonComment: string;
    @component reasonCode: ReasonCodeData;

    get description(): string {
        if (this.reasonCode.code) {
            return this.reasonCode.code + (this.reasonComment ? " - " + this.reasonComment : "");
        } else {
            return this.reasonComment;
        }
    }
}

export class PriceModifierData extends EntityData<PriceModifierData> {
    @field dollarAmount: number = 0;
}

export class SaleLineData extends EntityData<SaleLineData> {
    static readonly SALE_LINE_TYPE_VOID = "VOID";

    @identifier saleLineId: string;
    @field sellableName: string;
    @field type: string;
    @field unitPrice: number;
    @field sellablePrice: number;
    @field qty: number;
    @field discAmt: number;
    @field costPrice: number;
    @field costTax: number;
    @field subTotal: number;
    @field totalTax: number;
    @field refundedQty: number;
    @component discountReason: ReasonData;

    get extrasPrice(): number {
        return this.sellablePrice - this.unitPrice;
    }

    get discountedPricePerItem(): number {
        return this.sellablePrice - NumberUtils.roundValue( this.discAmt / this.qty, 2);
    }

    get costEx(): number {
        return this.costPrice - this.costTax;
    }

    get subTotalEx(): number {
        return this.subTotal - this.totalTax;
    }

    get marginEx(): number {
        return this.subTotal - this.totalTax - this.costPrice - this.costTax;
    }

    get marginPercentEx(): number {
        return NumberUtils.roundValue(100 * this.marginEx / (this.subTotalEx === 0 ? 1 : this.subTotalEx), 2);
    }
}

export class PaymentAccountMetaData extends EntityData<PaymentAccountMetaData> {
    @field accountNo: string;
    @field accountName: string;
}

export class SalePaymentData extends EntityData<SalePaymentData> {
    static readonly PAYMENT_TYPE_SALE = "SALE";
    static readonly PAYMENT_TYPE_VOID = "VOID";

    @identifier paymentId: string;
    @field amount: number;
    @field change: number;
    @field cashout: number;
    @field tip: number;
    @field paymentType: string;
    @field paymentMethod: string;
    @field method: string;
    @field details: string;
    @field reference: string;
    @field platformAccountId: string;
    @component reason: ReasonData;
    @component paymentAccountMetaData: PaymentAccountMetaData;
}

export class RecUserData extends EntityData<RecUserData> {
    @field firstName: string;
    @field lastName: string;

    get name(): string {
        return (this.firstName || "") + " " + (this.lastName || "");
    }
}

export class RecMemberData extends EntityData<RecMemberData> {
    @field name: string;
}

export class LoyaltyAccrualData extends EntityData<LoyaltyAccrualData> {
    @component recMember: RecMemberData;
    @field pointsAccrued: number;
}

export class SaleIntegratedWebOrderData extends EntityData<SaleIntegratedWebOrderData> {
    @identifier integratedOrderId: string;
    @field status: string;
    @field posOrderId: string;
    @field orderTotal: number;
    @dateField orderSyncDate: Date;
    @field orderProcessorName: string;
}

export class SalesJournalData extends EntityData<SalesJournalData> {
    @identifier saleId: string;
    @field receiptNo: string;
    @field identifier: string;
    @field orderName: string;
    @field customerRefNo: string;
    @field customerFirstName: string;
    @field customerLastName: string;
    @dateField saleDate: Date;
    @field terminalName: string;
    @field saleType: string;
    @component refundReason: ReasonData;
    @component discountReason: ReasonData;
    @field saleTotal: number;
    @field totalCost: number;
    @field totalCostTax: number;
    @field totalSalesTax: number;
    @field rounding: number;
    @field saleSurcharge: number;
    @field outletId: number;
    @field isAppOrder: boolean;
    @field isSaleOnOpenFloat: boolean;
    @field isSaleFromApi: boolean;
    @field integratedDeliveryId: string;
    @field integratedDeliveryTrackingUrl: string;
    @component recUser: RecUserData;
    @component saleDiscount: PriceModifierData;
    @component surcharge: PriceModifierData;
    @component accrual: LoyaltyAccrualData;
    @collection(SalePaymentData) payments: SalePaymentData[];
    @collection(SaleLineData) saleLines: SaleLineData[];

    get customerName(): string {
        return (this.customerFirstName ? this.customerFirstName + " " : "") + (this.customerLastName || "");
    }

    get tableNo(): string {
        return this.saleType === "Table Sale" || this.saleType === "App Sale at Table" ? this.orderName : undefined;
    }

    get totalCostEx(): number {
        return this.totalCost - this.totalCostTax;
    }

    get saleTotalEx(): number {
        return this.saleTotal - this.totalSalesTax;
    }

    get marginEx(): number {
        return this.saleTotalEx - this.totalCostEx;
    }

    get marginExPerc(): number {
        return NumberUtils.roundValue(100 * this.marginEx / (this.saleTotalEx === 0 ? 1 : this.saleTotalEx), 2);
    }

    get paymentTotal(): number {
        return this.payments ? NumberUtils.roundValue(this.payments.map((payment) => payment.amount - payment.change).reduce((total: number, value: number) => total + value, 0), 2) : 0;
    }

    get paymentVoidTotal(): number {
        if (this.payments) {
            return this.payments.filter((payment) => payment.paymentType === SalePaymentData.PAYMENT_TYPE_VOID)
                .map((payment) => payment.amount - payment.change).reduce((total: number, value: number) => total + value, 0);
        } else {
            return 0;
        }
    }

    get saleTotalPlusRounding(): number {
        return this.saleTotal + this.rounding;
    }

    get saleLineDiscounts(): number {
        if (this.saleLines) {
            return this.saleLines.map((saleLine) => saleLine.discAmt).reduce((total, value) => total + value, 0);
        } else {
            return 0;
        }
    }

    get saleLineVoidTotal(): number {
        if (this.saleLines) {
            return this.saleLines.filter((saleLine) => saleLine.type === SaleLineData.SALE_LINE_TYPE_VOID)
                .map((saleLine) => saleLine.subTotal).reduce((total, value) => total + value, 0);
        } else {
            return 0;
        }
    }

    get saleLineTotal(): number {
        if (this.saleLines) {
            return this.saleLines.map((saleLine) => saleLine.subTotal).reduce((total, value) => total + value, 0);
        } else {
            return 0;
        }
    }
}
